import { dateFormat, getAge } from "../../core/utils/date-utils";
import { Button, Checkbox, Col, Divider, Form, Input, Modal, Popover, Row, Select, Space, Tooltip, Typography } from "antd";
import ReportEditor from "../../core/components/ReportEditor";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import ApiService from "../../core/service/api-service";
import Flex from "../../core/components/Flex";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { Scrollbars } from "react-custom-scrollbars";
import { useApp } from "../../core/context/AppContext";

export default function ReportCreateEditForm({ studyId, request, onSaved, title }) {
    const { t } = useTranslation();
    const { hasPermission } = useUser()
    const { addKeyEvent, removeKeyEvent, pressedKey } = useApp()
    const formRef = useRef();
    const [autoClose, setAutoClose] = useState(localStorage.getItem('autoClose') || false)
    const [confirmModalVisible, setConfirmModalVisible] = useState(false)
    const [confirmForm] = Form.useForm()

    const [_reportId, setReportId] = useState(request?.request?.reportId)
    const [readUsers, setReadUsers] = useState([])
    const [templates, setTemplates] = useState([])
    const [template, setTemplate] = useState({ content: {} });
    const [reportContent, setReportContent] = useState({});
    const [report, setReport] = useState({
        request: {
            requestType: {}
        }
    })
    const reportSaveContent = useRef({})

    const getReadUsers = async () => {
        const res = await ApiService.StudyDetails.GetReadUsersOfStudy([studyId]);
        if (res.success) {
            setReadUsers(res.data)
        }
    }

    const loadReportFileData = async (studyId, requestId, reportId) => {
        try {
            const response = await ApiService.Report.GetReportFileData(studyId, requestId, reportId);
            if (response.success) {
                const content = JSON.parse(response.data);
                setReportContent(content)
                reportSaveContent.current = content;
            }
        }
        catch (exp) {
            console.log(exp)
            setReportContent({})
        }
    }

    const onTemplateChange = async (val, option) => {
        setTemplate(option)
        await loadReportTemplateFile(option.id);
    }

    const loadReportTemplateFile = async (templateId) => {
        try {
            if (templateId) {
                const response = await ApiService.ReportTemplate.GetReportTemplateFileData(templateId);
                if (response.success) {
                    setReportContent(JSON.parse(response.data))
                    reportSaveContent.current = JSON.parse(response.data)
                }
            }
            else {
                setReportContent({})
            }
        }
        catch (e) {
            setReportContent({})
        }
    }

    const loadReport = async (requestId, reportId) => {
        if (request?.id && request?.reportId) {
            const response = await ApiService.Report.Get(studyId, requestId, reportId)
            if (response.success) {
                setTemplate({})
                setReport(response.data)
                await loadReportFileData(studyId, requestId, reportId)
            }
        }
    }

    useEffect(() => {
        if (report?.studyReadUser?.id) {
            formRef.current.setFieldsValue({
                studyReadUserId: report?.studyReadUser?.id
            })
        }
        else if (readUsers?.length === 1) {
            formRef.current.setFieldsValue({
                studyReadUserId: readUsers[0].id
            })
        }
        else if (readUsers?.length > 1) {
            formRef.current.setFieldsValue({
                studyReadUserId: readUsers[readUsers.length - 1]?.id
            })
        }
    }, [readUsers])

    const previewPdf = async () => {
        const response = await ApiService.ReportGenerator.GetReportPreviewPdf(studyId, request?.id, request?.reportId, {
            ...reportSaveContent.current
        })

        if (response.success) {
            console.log(response.data)
            const pdfBase64 = response.data.pdfFile;
            const pdfWindow = window.open("");
            pdfWindow.document.write(
                `<iframe width='100%' height='100%' src='data:application/pdf;base64,${pdfBase64}'></iframe>`
            );
        }
    }

    const save = async (isConfirm, confirmDescription = null) => {
        if (!request?.id || request?.reportIsConfirm)
            return

        if (!hasPermission(PERMISSIONS.REPORT_CONFIRM) && isConfirm)
            return

        const text = formRef.current.getFieldsValue().text;
        const studyReadUserId = formRef.current.getFieldsValue().studyReadUserId;

        if (text)
            await ApiService.StudyAnamnez.EditClinicalInformation(studyId, text)

        if (request?.reportId) {
            const response = await ApiService.Report.Edit(studyId, request?.id, request?.reportId, {
                content: reportSaveContent.current,
                studyReadUserId,
                isConfirm,
                language: template.language || report?.language,
                description: confirmDescription
            })

            response.success && onSaved && onSaved(studyId, request?.id, response.data);
            await loadReport(request?.id, response.data)
        } else {
            const response = await ApiService.Report.Create(studyId, request?.id, {
                content: reportSaveContent.current,
                studyReadUserId,
                isConfirm,
                language: template.language || report?.language,
                description: confirmDescription
            })

            response.success && onSaved && onSaved(studyId, request?.id, response.data);
            await loadReport(request?.id, response.data)
        }
    }

    const handleSaveAndConfirmClick = async () => {
        if (request?.studyTasks && request.studyTasks.length > 0) {
            setConfirmModalVisible(true);
        } else {
            await save(true);
        }
    }

    const handleConfirmWithDescription = async () => {
        try {
            const values = await confirmForm.validateFields();
            await save(true, values.description);
            setConfirmModalVisible(false);
            confirmForm.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    const getReportTemplates = async (studyId, requestId) => {
        const response = await ApiService.ReportTemplate.GetList(studyId, requestId);
        if (response.success) {
            return response.data.map(t => {
                return {
                    ...t,
                    value: t.id,
                    label: t.name
                }
            })
        }

        return []
    }

    const init = async () => {
        if (!request?.id)
            return
        await getReadUsers()
        const templates = await getReportTemplates(studyId, request?.id)
        setTemplates(templates)

        if (request?.reportId) {
            await loadReport(request?.id, request?.reportId)
        }
        else {
            let templateId = templates[0]?.id;
            await loadReportTemplateFile(templateId)
        }
    }

    useEffect(() => {
        init()
    }, [request])

    useEffect(() => {
        addKeyEvent([
            { keyCode: 'KeyQ', modifier: 'ControlLeft', prevent: false, },
            { keyCode: 'KeyQ', modifier: 'AltLeft', prevent: false, },
            // {keyCode: 'F4', modifier: 'ControlLeft', prevent: true,},
        ])

        return () => {
            removeKeyEvent([
                { keyCode: 'KeyQ', modifier: 'ControlLeft', prevent: false, },
                { keyCode: 'KeyQ', modifier: 'AltLeft', prevent: false, },
            ])
        }
    }, []);

    useEffect(() => {
        if (pressedKey?.key?.code === 'KeyQ' && pressedKey?.modifier?.code === 'ControlLeft') {
            // console.log('Pressed CRTL+KeyQ')
            handleSaveAndConfirmClick()
        }
        else if (pressedKey?.key?.code === 'KeyQ' && pressedKey?.modifier?.code === 'AltLeft') {
            save(false)
        }
    }, [pressedKey]);

    return (
        <>
            <Form
                ref={formRef}
                layout={'vertical'}
            >
                <Space wrap={true}>
                    <Form.Item>
                        <Select
                            disabled={!request?.id || request?.reportIsConfirm}
                            placeholder={t('Report Template')}
                            value={template.value}
                            style={{ minWidth: 200 }}
                            options={templates}
                            onChange={onTemplateChange}
                        />
                    </Form.Item>
                    <Form.Item
                        name={'studyReadUserId'}
                        label={''}
                        required={true}
                    >
                        <Select
                            disabled={!request?.id || request?.reportIsConfirm}
                            style={{ width: 150 }}
                            placeholder={t('Read User')}
                        >
                            {
                                readUsers.map(x => (
                                    <Select.Option key={x.id} value={x.id}>
                                        {x?.user?.visibleName}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button
                            disabled={!request?.id || request?.reportIsConfirm}
                            onClick={async () => {
                                await save(false)
                            }}
                            type={'primary'}>
                            {t('Save') + ' (Alt + Q)'}
                        </Button>
                    </Form.Item>
                    {
                        hasPermission(PERMISSIONS.REPORT_CONFIRM) && (
                            <Form.Item>
                                <Button
                                    disabled={!request?.id || request?.reportIsConfirm}
                                    onClick={handleSaveAndConfirmClick}
                                >
                                    {t('Save And Confirm') + ' (Ctrl + Q)'}
                                </Button>
                            </Form.Item>
                        )
                    }
                    <Form.Item>
                        <Button
                            disabled={!request?.id}
                            onClick={previewPdf}
                            type={'primary'}>
                            {t('Preview')}
                        </Button>
                    </Form.Item>
                </Space>

                {request?.studyTasks && request.studyTasks.length > 0 && !request?.reportIsConfirm && (
                    <div style={{ marginTop: 12, marginBottom: 12, padding: 12, backgroundColor: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
                        <strong style={{ color: '#d46b08' }}>{t('Open Tasks')}:</strong>
                        {request.studyTasks.map((task, idx) => (
                            <div key={idx} style={{ marginTop: 8 }}>
                                <div style={{ fontWeight: 500 }}>{t(task.taskType === 2 ? 'RE_ASSESSMENT' : 'Task')}</div>
                                {task.description && <div style={{ fontSize: 12, color: '#666' }}>{task.description}</div>}
                            </div>
                        ))}
                    </div>
                )}

                <Scrollbars
                    autoHide
                    style={{
                        height: 'calc(100vh - 200px)',
                    }}
                >
                    <div
                        style={{
                            border: '1px solid #cfcfcf',
                            padding: 4,
                            marginRight: 16
                        }}
                    >
                        <ReportEditor
                            editable={request?.id}
                            defaultReportContent={request?.id ? reportContent : {}}
                            onBulgular={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Bulgular: data
                                }
                            }}
                            onSonuc={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    SonucVeOneriler: data
                                }
                            }}
                            onKarsilastirma={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Karsilastirma: data
                                }
                            }}
                            onTeknik={(data) => {
                                reportSaveContent.current = {
                                    ...reportSaveContent.current,
                                    Teknik: data
                                }
                            }}
                        />
                    </div>
                </Scrollbars>
            </Form>
            <Modal
                title={t('Confirm Report - Open Task Exists')}
                open={confirmModalVisible}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    confirmForm.resetFields();
                }}
                onOk={handleConfirmWithDescription}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
            >
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
                    <strong>{t('Open Tasks')}:</strong>
                    {request?.studyTasks?.map((task, idx) => (
                        <div key={idx} style={{ marginTop: 8 }}>
                            <div style={{ fontWeight: 500 }}>{t(task.taskType === 2 ? 'RE_ASSESSMENT' : 'Task')}</div>
                            {task.description && <div style={{ fontSize: 12, color: '#666' }}>{task.description}</div>}
                        </div>
                    ))}
                </div>
                <Form form={confirmForm} layout="vertical">
                    <Form.Item
                        name="description"
                        label={t('Please explain how the open task(s) will be resolved')}
                        rules={[{ required: true, message: t('Please enter description') }]}
                    >
                        <Input.TextArea rows={4} placeholder={t('Explain how you resolved or will handle the open task(s)')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}
